import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError, to12h, todayStr, dateStr, TIME_SLOTS } from '../../lib/api';
import {
  IndianRupee, Wallet, AlertCircle, ShoppingBag, UserPlus, RefreshCw, Waves, CalendarCheck,
  Plus, Loader2, Eye,
} from 'lucide-react';

const SERVICE_META = {
  general: { name: 'General Swimming Pool', price: 300 },
  endurance: { name: 'Endurance Training', price: 999 },
  jacuzzi: { name: 'Jacuzzi', price: 500 },
  therapy: { name: 'Water Therapy', price: 999 },
};

const inr = (n) => `\u20B9${(n || 0).toLocaleString('en-IN')}`;

const monday = () => { const d = new Date(); const off = (d.getDay() + 6) % 7; d.setDate(d.getDate() - off); return dateStr(d); };
const firstOfMonth = () => { const d = new Date(); return dateStr(new Date(d.getFullYear(), d.getMonth(), 1)); };

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'custom', label: 'Custom' },
];

const StatCard = ({ label, value, icon: Icon, gradient, testid }) => (
  <Card className={`shadow-sm border-0 bg-gradient-to-r ${gradient} text-white`}>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm">{label}</p>
          <p className="text-2xl font-bold" data-testid={testid}>{value}</p>
        </div>
        <Icon className="h-7 w-7 opacity-80" />
      </div>
    </CardContent>
  </Card>
);

const OverviewPage = () => {
  const [preset, setPreset] = useState('today');
  const [customStart, setCustomStart] = useState(todayStr());
  const [customEnd, setCustomEnd] = useState(todayStr());
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walkInForm, setWalkInForm] = useState({ customerName: '', phone: '', service: '', timeSlot: '' });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const range = useMemo(() => {
    if (preset === 'today') return { start: todayStr(), end: todayStr() };
    if (preset === 'week') return { start: monday(), end: todayStr() };
    if (preset === 'month') return { start: firstOfMonth(), end: todayStr() };
    return { start: customStart, end: customEnd };
  }, [preset, customStart, customEnd]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, bk] = await Promise.all([
        api.get('/analytics/overview', { params: { start: range.start, end: range.end } }),
        api.get('/bookings', { params: { start_date: range.start, end_date: range.end, limit: 100 } }),
      ]);
      setData(ov.data);
      setBookings(bk.data);
    } catch (err) {
      toast({ title: 'Failed to load dashboard', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [range, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleWalkIn = async () => {
    if (!walkInForm.customerName || !walkInForm.phone || !walkInForm.service || !walkInForm.timeSlot) {
      toast({ title: 'Missing Information', description: 'Please fill all fields.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/bookings', {
        booking_type: 'day_pass', service_id: walkInForm.service, date: todayStr(),
        start_time: walkInForm.timeSlot, name: walkInForm.customerName, phone: walkInForm.phone, is_walkin: true,
      });
      toast({ title: 'Walk-in added! 🎉', description: `${walkInForm.customerName} - ${SERVICE_META[walkInForm.service]?.name}` });
      setWalkInForm({ customerName: '', phone: '', service: '', timeSlot: '' });
      loadData();
    } catch (err) {
      toast({ title: 'Failed', description: formatApiError(err), variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const handleCancel = async (id) => {
    try { await api.post(`/bookings/${id}/cancel`); toast({ title: 'Booking cancelled' }); loadData(); }
    catch (err) { toast({ title: 'Cancel failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  return (
    <div data-testid="admin-overview">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Overview</h1>
          <p className="text-gray-500 text-sm mt-1">{range.start === range.end ? range.start : `${range.start} → ${range.end}`}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex bg-white rounded-lg p-1 shadow-sm border border-gray-100" data-testid="overview-date-filter">
            {PRESETS.map((p) => (
              <button key={p.key} onClick={() => setPreset(p.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium ${preset === p.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                data-testid={`preset-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex items-center gap-2" data-testid="custom-range">
              <Input type="date" value={customStart} max={customEnd} onChange={(e) => setCustomStart(e.target.value)} className="w-40 h-9" data-testid="custom-start" />
              <span className="text-gray-400 text-sm">to</span>
              <Input type="date" value={customEnd} min={customStart} max={todayStr()} onChange={(e) => setCustomEnd(e.target.value)} className="w-40 h-9" data-testid="custom-end" />
            </div>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-24" data-testid="overview-loading"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Sales Amount" value={inr(data.sales_amount)} icon={IndianRupee} gradient="from-blue-600 to-cyan-600" testid="ov-sales" />
            <StatCard label="Collection" value={inr(data.collection)} icon={Wallet} gradient="from-emerald-600 to-green-600" testid="ov-collection" />
            <StatCard label="Pending Payment" value={inr(data.pending_payment)} icon={AlertCircle} gradient="from-rose-600 to-red-600" testid="ov-pending" />
            <StatCard label="Accessory Sales" value={inr(data.accessory_sales)} icon={ShoppingBag} gradient="from-amber-500 to-orange-500" testid="ov-accessory" />
            <StatCard label="New Clients" value={data.new_clients} icon={UserPlus} gradient="from-indigo-600 to-purple-600" testid="ov-new-clients" />
            <StatCard label="Renewals" value={data.renewals} icon={RefreshCw} gradient="from-teal-600 to-cyan-600" testid="ov-renewals" />
            <StatCard label="Check-ins" value={data.checkins} icon={Waves} gradient="from-sky-600 to-blue-600" testid="ov-checkins" />
            <StatCard label="Bookings" value={data.bookings_count} icon={CalendarCheck} gradient="from-violet-600 to-fuchsia-600" testid="ov-bookings" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card className="shadow-md border-0 bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Eye className="h-5 w-5" /> Bookings
                    <Badge className="bg-blue-100 text-blue-800">{bookings.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {bookings.length > 0 ? (
                    <div className="space-y-3 max-h-[520px] overflow-auto" data-testid="ov-bookings-list">
                      {bookings.map((b) => (
                        <div key={b.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:shadow-sm">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold text-gray-900 text-sm">{b.name || b.swimmer_name || 'Guest'}</h3>
                              <Badge className={b.is_walkin ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>{b.is_walkin ? 'Walk-in' : 'Online'}</Badge>
                              {b.payment_status === 'paid'
                                ? <Badge className="bg-emerald-100 text-emerald-800">Paid</Badge>
                                : b.status !== 'cancelled' && <Badge className="bg-rose-100 text-rose-800">Pending</Badge>}
                              <Badge className={b.status === 'confirmed' ? 'bg-green-100 text-green-800' : b.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>{b.status}</Badge>
                            </div>
                            <p className="text-xs text-gray-500">{b.service_name || b.booking_type} · {b.date} {to12h(b.start_time)} {b.phone ? `· ${b.phone}` : ''}</p>
                          </div>
                          <div className="text-right ml-3">
                            <div className="font-bold text-gray-900">{inr(b.amount)}</div>
                            <div className="text-[10px] text-gray-400">{b.booking_code}</div>
                            {['confirmed', 'pending'].includes(b.status) && (
                              <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600" onClick={() => handleCancel(b.id)}>Cancel</Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 text-gray-500" data-testid="ov-bookings-empty">No bookings in this period.</div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div>
              <Card className="shadow-md border-0 bg-white">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plus className="h-5 w-5" /> Add Walk-in (Today)</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2"><Label>Customer Name</Label><Input value={walkInForm.customerName} onChange={(e) => setWalkInForm((p) => ({ ...p, customerName: e.target.value }))} placeholder="Name" data-testid="ov-walkin-name" /></div>
                  <div className="space-y-2"><Label>Phone</Label><Input value={walkInForm.phone} onChange={(e) => setWalkInForm((p) => ({ ...p, phone: e.target.value }))} placeholder="Phone" data-testid="ov-walkin-phone" /></div>
                  <div className="space-y-2">
                    <Label>Service</Label>
                    <Select value={walkInForm.service} onValueChange={(v) => setWalkInForm((p) => ({ ...p, service: v }))}>
                      <SelectTrigger data-testid="ov-walkin-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                      <SelectContent>{Object.entries(SERVICE_META).map(([id, m]) => (<SelectItem key={id} value={id}>{m.name} ({inr(m.price)})</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Time Slot</Label>
                    <Select value={walkInForm.timeSlot} onValueChange={(v) => setWalkInForm((p) => ({ ...p, timeSlot: v }))}>
                      <SelectTrigger data-testid="ov-walkin-time"><SelectValue placeholder="Select time" /></SelectTrigger>
                      <SelectContent>{TIME_SLOTS.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleWalkIn} disabled={submitting} className="w-full bg-gradient-to-r from-purple-600 to-pink-600" data-testid="ov-walkin-submit">
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add Walk-in
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OverviewPage;
